import { ArrowLeft, ChevronDown } from "lucide-react";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import welcomeBg from "../../assets/homebg.png";
import { useState } from "react";

const FAQsPage = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: "How do I log a meal?",
      answer:
        "To log a meal, tap the camera icon on the home screen and either scan your food or manually search for items. You can also use voice input to quickly add meals. The AI will analyze your food and automatically calculate nutritional information.",
    },
    {
      question: "How accurate is the calorie tracking?",
      answer:
        "Our calorie tracking uses advanced AI technology and a comprehensive food database to provide highly accurate nutritional information. The accuracy is typically within 5-10% for most foods. For best results, try to be specific when logging portion sizes.",
    },
    {
      question: "Can I connect my Fitbit or other devices?",
      answer:
        "Yes! You can connect various fitness devices including Fitbit, Apple Health, Google Fit, and other popular wearables. Go to Profile > Connected Devices to link your devices and automatically sync your activity, sleep, and health data.",
    },
    {
      question: "How do I change my health goals?",
      answer:
        "You can update your health goals anytime by going to Profile > Goals. Here you can adjust your target weight, daily calorie goals, macronutrient ratios, and activity levels. The app will automatically recalibrate your recommendations based on your new goals.",
    },
    {
      question: "What is the Bite Balance Score?",
      answer:
        "The Bite Balance Score is a comprehensive metric that evaluates your overall nutritional balance, meal timing, food quality, and lifestyle factors. It ranges from 0-100 and helps you understand how well you're meeting your health goals. A higher score indicates better nutritional balance.",
    },
    {
      question: "Can I export my health data?",
      answer:
        "Yes, you can export all your health data including meal logs, nutrition history, activity data, and health metrics. Go to Profile > Privacy & Security > Export Data to download your information in CSV or PDF format.",
    },
    {
      question: "Is my health data secure?",
      answer:
        "Absolutely. We use industry-standard encryption to protect your data both in transit and at rest. Your health information is stored securely and never shared with third parties without your explicit consent. You can review our privacy policy in the app settings.",
    },
    {
      question: "How do I contact support?",
      answer:
        "You can reach our support team by emailing support@bitebalance.com or through the in-app contact form at Profile > Help & Support. We typically respond within 24 hours on business days.",
    },
  ];

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-6 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center gap-4 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title2 font-bold text-foreground">FAQs</h1>
        </div>

        {/* Description */}
        <p className="text-subhead text-muted-foreground">
          Find answers to the most commonly asked questions about Bite Balance.
        </p>

        {/* FAQ Accordion */}
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="flex w-full items-center justify-between p-4 text-left transition-colors hover:bg-accent/50"
              >
                <span className="font-medium text-foreground">
                  {faq.question}
                </span>
                <ChevronDown
                  className={`h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform ${
                    openIndex === index ? "rotate-180" : ""
                  }`}
                />
              </button>
              {openIndex === index && (
                <div className="border-t border-border bg-card px-4 pb-4 pt-3">
                  <p className="text-subhead text-muted-foreground">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="pt-4 text-center text-subhead text-muted-foreground">
          Still have questions? Email us at support@bitebalance.com
        </div>
      </div>
    </div>
  );
};

export default FAQsPage;
